'use client';
import { useMemo } from 'react';
import styled from 'styled-components';

import { Item, type ItemProps, NavBar, type NavBarProps } from '../../atoms/nav-bar/NavBar';

type ItemTabProps = Omit<ItemProps, 'children'> & {
	icon?: string;
	id: string;
	text: string;
};

export type ControlledSegmentedControlProps = Omit<NavBarProps, 'children' | 'onChangeSelected' | 'selected'> & {
	className?: string;
	items: Readonly<Array<ItemTabProps>>;
	onChangeSelected: (newSelection: string) => void;
	selected: string;
	testID: string;
};
/**
 * ControlledSegmentedControl component
 * @param {Array<ItemTabProps>} items array of Items, text and Id
 * @param {string} testID
 * @param {string} selected The selected tab
 * @param {Function} onChangeSelected Called when the selected tab should change
 * @param props Rest of props
 *
 */
export const ControlledSegmentedControl = ({
	items = [],
	onChangeSelected,
	selected,
	testID,
	...args
}: ControlledSegmentedControlProps) => {
	const fixedItemsMapped = useMemo(
		() =>
			items.map(({ id, text, ...itemProps }) => {
				const isSelected = id === selected;

				return (
					<S.FixedItem $selected={isSelected} {...itemProps} data-testid={`${testID}-${id}`} id={id} key={id}>
						{itemProps.icon && (
							<img alt="segmented control icon" height={20} key="icon" src={itemProps.icon} width={20} />
						)}
						<S.TextItem $selected={isSelected} key="text">
							{text}
						</S.TextItem>
					</S.FixedItem>
				);
			}),
		[items, selected, testID]
	);

	return (
		<S.SegmentedControlFixed data-testid={testID} selected={selected} onChangeSelected={onChangeSelected} {...args}>
			{fixedItemsMapped}
		</S.SegmentedControlFixed>
	);
};

namespace S {
	export const SegmentedControlFixed = styled(NavBar)`
		margin: 0;
		padding: ${({ theme }) => theme.spacings.xxxsmall};
		flex: none;
		display: flex;
		justify-content: space-around;
		align-items: center;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.unselected};
		margin: ${({ theme }) => theme.spacings.xsmall};
		${({ theme }) => theme.border.radius.circle}
	`;

	export const FixedItem = styled(Item)<{ $selected: boolean }>`
		flex: none;
		justify-content: center;
		margin: 0;
		max-height: auto;
		padding: ${({ theme }) => theme.spacings.xxxsmall};
		flex-grow: 1;
		cursor: ${({ $selected }) => ($selected ? 'unset' : 'pointer')};
		flex-wrap: nowrap;
		background: ${({ $selected, theme }) =>
			$selected ? theme.colors.backgrounds.main.container : theme.colors.backgrounds.main.unselected};
		${({ theme }) => theme.border.radius.circle}
	`;

	export const TextItem = styled.div<{ $selected: boolean }>`
		${({ theme }) => theme.fonts.text.text4};
		padding: 0;
		color: ${({ $selected, theme }) =>
			$selected ? theme.colors.text.main.enabled : theme.colors.text.main.disabled};
		margin: ${({ theme }) => theme.spacings.xxsmall};
	`;
}
